/**
 * Sentry observability — captures unhandled JS errors + rejections.
 *
 * Fail-open: if REACT_APP_SENTRY_DSN is unset or empty, Sentry is skipped
 * silently. Local dev / CI stay quiet; production captures issues when the
 * client sets the DSN at build time.
 *
 * Privacy:
 * - sendDefaultPii=false — no cookies/IPs
 * - beforeSend drops handled 401/403 axios errors (flagged __handled by
 *   lib/api.js) and scrubs Authorization / password-like headers
 */
import * as Sentry from "@sentry/react";

const dsn = (process.env.REACT_APP_SENTRY_DSN || "").trim();
const environment =
  process.env.REACT_APP_SENTRY_ENVIRONMENT ||
  (process.env.NODE_ENV === "production" ? "production" : "development");
const release = (process.env.REACT_APP_SENTRY_RELEASE || "").trim() || undefined;

let enabled = false;

if (dsn) {
  Sentry.init({
    dsn,
    environment,
    release,
    sendDefaultPii: false,
    tracesSampleRate: 0,

    beforeSend(event, hint) {
      // Swallow handled 401/403 axios errors (lib/api.js tags them)
      const original = hint && hint.originalException;
      if (original && original.__handled === true) return null;

      if (event.request) {
        delete event.request.data;
        delete event.request.query_string;
        if (event.request.headers) {
          for (const key of Object.keys(event.request.headers)) {
            if (/authorization|cookie|token|password|secret/i.test(key)) {
              event.request.headers[key] = "[Filtered]";
            }
          }
        }
      }
      return event;
    },
  });
  enabled = true;
}

/**
 * Identify a user with Sentry after successful login.
 * Pass null on logout to clear the user context.
 */
export function identifySentryUser(user) {
  if (!enabled) return;
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: String(user.id || ""),
    email: user.email,
    role: user.role,
  });
}

export const isSentryEnabled = () => enabled;
export { Sentry };
